import { ReactNode, useState } from "react";
import TableSyncDialog, { SyncAction, SyncItem } from "admin/components/TableSyncDialog";
import useAdminSchemas, { ADMIN_SCHEMAS_LIMIT } from "admin/hooks/useAdminSchemas";
import useAdminTableDiff from "admin/hooks/useAdminTableDiff";
import {
  MissingFromOMItem,
  OMTableStatus,
  OrphanedInOMItem,
  StaleInOMItem,
} from "admin/types/interfaces/adminTableDiff";
import { format, parseISO } from "date-fns";
import { CircleAlert, CircleCheck, Clock, Ghost, Info, Loader2 } from "lucide-react";
import { Badge, BadgeProps } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import Combobox from "~/components/ui/combobox";
import { Label } from "~/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { StatsCard } from "~/components/ui/stats-card";
import { cn } from "~/lib/utils";
import { isApiError } from "~/utils/apiClient";

const formatDate = (dateString: string) => format(parseISO(dateString), "MM/dd/yy h:mmaaa");

/** Per-user personal schemas, one per platform user, should not be diffed/synced here. */
const PERSONAL_SCHEMA_PREFIX = "USER_";

/** Per-dataset dedicated schemas — numerous, so opt-in behind a checkbox. */
const DATASET_SCHEMA_PREFIX = "DATASET_";

/** Snowflake's own metadata schema, never a diff target. */
const SNOWFLAKE_METADATA_SCHEMA = "INFORMATION_SCHEMA";

/** Verbiage for the per-column info popovers. */
const COLUMN_INFO = {
  missingFromOm:
    "Tables in Snowflake that the Manifold platform has no valid registration for: never registered, registered but soft-deleted, or registered with a different case. Users can't find these tables in the catalog.",
  staleInOm:
    "Tables which are registered but may be out of date in the Manifold platform. The staleness may be benign, or may indicate that Manifold's metadata needs updates.",
  inSync: "Tables which are properly registered and up to date. No action needed.",
  orphanedInOm:
    "Tables which are registered and active in the Manifold platform but no longer exist in Snowflake. These still appear in search and dataset listings even though SQL queries against them fail.",
} as const;

const isHiddenSchema = (name: string, showDatasetSchemas: boolean) => {
  const upper = name.toUpperCase();
  return (
    upper === SNOWFLAKE_METADATA_SCHEMA ||
    upper.startsWith(PERSONAL_SCHEMA_PREFIX) ||
    (!showDatasetSchemas && upper.startsWith(DATASET_SCHEMA_PREFIX))
  );
};

const OM_STATUS_LABEL: Record<OMTableStatus, string> = {
  [OMTableStatus.NOT_REGISTERED]: "Not registered",
  [OMTableStatus.SOFT_DELETED]: "Soft deleted",
  [OMTableStatus.CASE_MISMATCH]: "Case mismatch",
};

const OM_STATUS_VARIANT: Record<OMTableStatus, BadgeProps["variant"]> = {
  [OMTableStatus.NOT_REGISTERED]: "destructive",
  [OMTableStatus.SOFT_DELETED]: "warning",
  [OMTableStatus.CASE_MISMATCH]: "info",
};

/** Select-all state for one column's header checkbox. */
interface ColumnSelection {
  allSelected: boolean;
  someSelected: boolean;
  onToggleAll: (checked: boolean) => void;
}

interface DiffColumnProps {
  title: string;
  /** Shown on demand from the header's info button. */
  info: string;
  count: number;
  emptyText: string;
  /** Omitted for a column whose rows can't be synced. */
  selection?: ColumnSelection;
  children: ReactNode;
}

const DiffColumn = ({ title, info, count, emptyText, selection, children }: DiffColumnProps) => (
  <section
    aria-label={title}
    className="flex min-h-0 flex-col overflow-hidden rounded-lg border bg-background"
  >
    <header className="flex shrink-0 items-center justify-between gap-2 border-b px-4 py-3">
      <div className="flex min-w-0 items-center gap-1">
        {selection && count > 0 && (
          <Checkbox
            className="mr-1 shrink-0"
            aria-label={`Select all in ${title}`}
            checked={
              selection.allSelected ? true : selection.someSelected ? "indeterminate" : false
            }
            onCheckedChange={(checked) => selection.onToggleAll(!!checked)}
          />
        )}
        <h2 className="truncate text-sm font-semibold text-foreground">{title}</h2>
        {/* A Popover, not a Tooltip: this opens on click (and so works on touch),
            whereas a Radix tooltip dismisses on pointer-down. */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-6 shrink-0 text-muted-foreground"
              aria-label={`About ${title}`}
            >
              <Info className="size-3.5" />
            </Button>
          </PopoverTrigger>
          {/* Borrows the design system's tooltip treatment (`bg-foreground
              text-background`, inverted in both light and dark) so the panel
              reads as an overlay rather than another column card. */}
          <PopoverContent
            align="start"
            className="bg-foreground p-3 text-xs leading-relaxed text-background"
          >
            {info}
          </PopoverContent>
        </Popover>
      </div>
      <Badge variant="muted">{count}</Badge>
    </header>
    {count === 0 ? (
      <p className="px-4 py-8 text-center text-sm text-muted-foreground">{emptyText}</p>
    ) : (
      <ul className="min-h-0 flex-1 divide-y overflow-auto">{children}</ul>
    )}
  </section>
);

/** Per-row selection state, omitted for a row that can't be synced. */
interface RowSelection {
  selected: boolean;
  onChange: (selected: boolean) => void;
}

/** One row in a diff column: the table name, then whatever metadata the category carries. */
const DiffRow = ({
  name,
  icon,
  selection,
  children,
}: {
  name: string;
  /** Leading status adornment, for categories where every row shares one state. */
  icon?: ReactNode;
  selection?: RowSelection;
  children?: ReactNode;
}) => (
  <li className="flex items-start gap-2 px-4 py-2.5">
    {selection && (
      <Checkbox
        className="mt-0.5 shrink-0"
        aria-label={`Select ${name}`}
        checked={selection.selected}
        onCheckedChange={(checked) => selection.onChange(!!checked)}
      />
    )}
    <div className="min-w-0 flex-1">
      <div className="flex min-w-0 items-center gap-1.5">
        {icon}
        <p className="truncate font-mono text-sm font-medium text-foreground">{name}</p>
      </div>
      {children}
    </div>
  </li>
);

const MetaLine = ({ children, className }: { children: ReactNode; className?: string }) => (
  <p className={cn("truncate text-xs text-muted-foreground", className)}>{children}</p>
);

const MissingRow = ({ item, selection }: { item: MissingFromOMItem; selection: RowSelection }) => (
  <DiffRow name={item.table_name} selection={selection}>
    <div className="mt-1 flex flex-wrap items-center gap-1.5">
      <Badge variant={OM_STATUS_VARIANT[item.om_status]}>{OM_STATUS_LABEL[item.om_status]}</Badge>
      {item.snowflake_last_altered && (
        <MetaLine>Altered {formatDate(item.snowflake_last_altered)}</MetaLine>
      )}
    </div>
    {/* The OpenMetadata-side fqn is the whole story for a case mismatch, and the
        only way to spot a stray registration for the other statuses. */}
    <MetaLine className="mt-1">{item.fqn}</MetaLine>
  </DiffRow>
);

const StaleRow = ({ item, selection }: { item: StaleInOMItem; selection: RowSelection }) => (
  <DiffRow name={item.table_name} selection={selection}>
    <div className="mt-1 flex flex-wrap items-center gap-1.5">
      <Badge variant="warning">{item.stale_by} behind</Badge>
    </div>
    <MetaLine className="mt-1">Snowflake DDL {formatDate(item.snowflake_last_ddl)}</MetaLine>
    <MetaLine>Manifold platform {formatDate(item.om_updated_at)}</MetaLine>
  </DiffRow>
);

const OrphanedRow = ({ item, selection }: { item: OrphanedInOMItem; selection: RowSelection }) => (
  <DiffRow name={item.table_name} selection={selection}>
    <div className="mt-1 flex flex-wrap items-center gap-1.5">
      {/* Blue, matching the Ghost icon on this category's summary tile. */}
      <Badge variant="info">Orphan</Badge>
      <MetaLine>Manifold platform {formatDate(item.om_updated_at)}</MetaLine>
    </div>
    <MetaLine className="mt-1">{item.fqn}</MetaLine>
  </DiffRow>
);

const TableDiff = () => {
  const [schema, setSchema] = useState<string | null>(null);
  const [showDatasetSchemas, setShowDatasetSchemas] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<ReadonlySet<string>>(new Set());
  const [isReviewing, setIsReviewing] = useState(false);
  const {
    items: schemas,
    metadata: schemasMetadata,
    isLoading: schemasLoading,
  } = useAdminSchemas();
  const { diff, isLoading, error, refresh } = useAdminTableDiff(schema);

  const schemaOptions = schemas
    .filter(({ name }) => !isHiddenSchema(name, showDatasetSchemas))
    .map(({ name }) => ({ value: name, label: name }));

  const clearSelection = () => setSelectedKeys(new Set());

  // A selection only means anything against the schema it was made in.
  const handleSchemaChange = (next: string) => {
    setSchema(next);
    clearSelection();
  };

  // On hide of the per-dataset schemas, reset the selection if it was a dataset schema.
  const handleShowDatasetSchemasChange = (checked: boolean) => {
    setShowDatasetSchemas(checked);
    if (!checked && schema?.toUpperCase().startsWith(DATASET_SCHEMA_PREFIX)) {
      setSchema(null);
      clearSelection();
    }
  };

  const setKeysSelected = (keys: string[], selected: boolean) =>
    setSelectedKeys((previous) => {
      const next = new Set(previous);
      for (const key of keys) {
        if (selected) {
          next.add(key);
        } else {
          next.delete(key);
        }
      }
      return next;
    });

  // Derived from the current diff rather than snapshotted at click time, so a table
  // that disappears from a category on refresh drops out of the selection with it.
  const syncItems: SyncItem[] = diff
    ? [
        ...diff.missing_from_om.map((item) => ({
          key: `${SyncAction.REGISTER}:${item.table_name}`,
          action: SyncAction.REGISTER,
          table: item.table_name,
          fqn: item.fqn,
          caseMismatch: item.om_status === OMTableStatus.CASE_MISMATCH,
        })),
        ...diff.stale_in_om.map((item) => ({
          key: `${SyncAction.REFRESH}:${item.table_name}`,
          action: SyncAction.REFRESH,
          table: item.table_name,
          fqn: item.fqn,
        })),
        ...diff.orphaned_in_om.map((item) => ({
          key: `${SyncAction.REMOVE}:${item.table_name}`,
          action: SyncAction.REMOVE,
          table: item.table_name,
          fqn: item.fqn,
        })),
      ]
    : [];

  const selectedItems = syncItems.filter((item) => selectedKeys.has(item.key));
  const removeCount = selectedItems.filter((item) => item.action === SyncAction.REMOVE).length;
  const registerCount = selectedItems.length - removeCount;

  const columnSelection = (action: SyncAction): ColumnSelection => {
    const keys = syncItems.filter((item) => item.action === action).map((item) => item.key);
    const selectedCount = keys.filter((key) => selectedKeys.has(key)).length;
    return {
      allSelected: keys.length > 0 && selectedCount === keys.length,
      someSelected: selectedCount > 0 && selectedCount < keys.length,
      onToggleAll: (checked) => setKeysSelected(keys, checked),
    };
  };

  const rowSelection = (action: SyncAction, table: string): RowSelection => {
    const key = `${action}:${table}`;
    return {
      selected: selectedKeys.has(key),
      onChange: (selected) => setKeysSelected([key], selected),
    };
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <div className="space-y-1.5">
        {/* Not a <label>: the trigger is a role="combobox" button whose accessible
            name already comes from its own content, so htmlFor has nothing to bind to. */}
        <p className="text-sm font-medium text-foreground">Choose a schema:</p>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <div className="w-full max-w-sm">
            <Combobox
              className="w-full"
              options={schemaOptions}
              value={schema}
              onChange={handleSchemaChange}
              loading={schemasLoading}
              placeholder="Select a schema"
              searchPlaceholder="Search schemas..."
              emptyText="No schemas found."
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="show-dataset-schemas"
              aria-label="Show per-dataset schemas"
              checked={showDatasetSchemas}
              onCheckedChange={(checked) => handleShowDatasetSchemasChange(!!checked)}
            />
            <Label htmlFor="show-dataset-schemas" className="font-normal">
              Show per-dataset schemas
            </Label>
          </div>
        </div>
        {schemasMetadata?.has_more && (
          <p className="text-xs text-muted-foreground">
            Showing the first {ADMIN_SCHEMAS_LIMIT} schemas.
          </p>
        )}
      </div>

      {!schema ? (
        <p className="text-sm text-muted-foreground">
          Pick a schema to compare its Snowflake tables against the Manifold platform.
        </p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {isApiError(error) && error.status === 404
            ? `Schema "${schema}" not found in Snowflake, or schema is not inspectable by the Catalog ingest Snowflake user.`
            : "Error comparing tables against the Manifold platform"}
        </p>
      ) : isLoading || !diff ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Comparing tables...</span>
        </div>
      ) : (
        <>
          {/* Named so the tile captions, which repeat the column headings
              verbatim, are still addressable on their own. */}
          <section aria-label="Diff summary">
            <div className="grid gap-2 sm:grid-cols-3 xl:grid-cols-5">
              <StatsCard
                className="border bg-muted"
                size="compact"
                title={diff.found_in_snowflake}
                description="Tables in Snowflake"
              />
              <StatsCard
                className="border bg-muted"
                size="compact"
                title={diff.found_in_openmetadata}
                description="Tables in Manifold platform"
              />
              <StatsCard
                className="border bg-muted"
                size="compact"
                title={diff.missing_from_om_count}
                description="Only in Snowflake"
                icon={CircleAlert}
                iconColor="text-destructive"
              />
              <StatsCard
                className="border bg-muted"
                size="compact"
                title={diff.stale_in_om_count}
                description="Updated in Snowflake"
                icon={Clock}
                iconColor="text-yellow-600"
              />
              <StatsCard
                className="border bg-muted"
                size="compact"
                title={diff.orphaned_in_om_count}
                description="Only in Manifold"
                icon={Ghost}
                iconColor="text-blue-600"
              />
            </div>
            <p className="px-4 text-xs text-muted-foreground">
              Checked {formatDate(diff.checked_at)}
            </p>
          </section>

          <div className="grid min-h-0 flex-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <DiffColumn
              title="Only in Snowflake"
              info={COLUMN_INFO.missingFromOm}
              count={diff.missing_from_om.length}
              emptyText="No missing tables"
              selection={columnSelection(SyncAction.REGISTER)}
            >
              {diff.missing_from_om.map((item) => (
                <MissingRow
                  key={item.fqn}
                  item={item}
                  selection={rowSelection(SyncAction.REGISTER, item.table_name)}
                />
              ))}
            </DiffColumn>

            <DiffColumn
              title="Updated in Snowflake"
              info={COLUMN_INFO.staleInOm}
              count={diff.stale_in_om.length}
              emptyText="No stale tables"
              selection={columnSelection(SyncAction.REFRESH)}
            >
              {diff.stale_in_om.map((item) => (
                <StaleRow
                  key={item.fqn}
                  item={item}
                  selection={rowSelection(SyncAction.REFRESH, item.table_name)}
                />
              ))}
            </DiffColumn>

            <DiffColumn
              title="In Sync"
              info={COLUMN_INFO.inSync}
              count={(diff.in_sync ?? []).length}
              emptyText="No tables in sync"
            >
              {(diff.in_sync ?? []).map((name) => (
                <DiffRow
                  key={name}
                  name={name}
                  icon={
                    <CircleCheck
                      role="img"
                      aria-label="In Sync"
                      className="size-3.5 shrink-0 text-green-600"
                    />
                  }
                />
              ))}
            </DiffColumn>

            <DiffColumn
              title="Only in Manifold"
              info={COLUMN_INFO.orphanedInOm}
              count={diff.orphaned_in_om.length}
              emptyText="No orphaned tables"
              selection={columnSelection(SyncAction.REMOVE)}
            >
              {diff.orphaned_in_om.map((item) => (
                <OrphanedRow
                  key={item.fqn}
                  item={item}
                  selection={rowSelection(SyncAction.REMOVE, item.table_name)}
                />
              ))}
            </DiffColumn>
          </div>

          {selectedItems.length > 0 && (
            <div className="flex shrink-0 flex-wrap items-center gap-3 rounded-lg border bg-muted px-4 py-3">
              {/* The summary doubles as the way to inspect the selection: it opens
                  the same review dialog the Sync button does, before committing. */}
              <Button
                variant="link"
                className="h-auto p-0 text-sm text-foreground"
                onClick={() => setIsReviewing(true)}
              >
                {selectedItems.length} selected · {registerCount} to register, {removeCount} to
                remove
              </Button>
              <div className="ml-auto flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={clearSelection}>
                  Clear
                </Button>
                <Button size="sm" onClick={() => setIsReviewing(true)}>
                  Sync...
                </Button>
              </div>
            </div>
          )}

          <TableSyncDialog
            schema={schema}
            items={selectedItems}
            open={isReviewing}
            onOpenChange={setIsReviewing}
            onDeselect={(key) => setKeysSelected([key], false)}
            onSyncComplete={() => {
              clearSelection();
              void refresh();
            }}
          />
        </>
      )}
    </div>
  );
};

export default TableDiff;
